import React from "react";
import { Avatar } from "@mui/material";
import Badge from "@mui/material/Badge";
import { styled } from "@mui/material/styles";

import CentricTheme from "../../../themes/CentricTheme";

import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import EventIcon from "@mui/icons-material/Event";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SourceIcon from "@mui/icons-material/Source";
import GppGoodIcon from "@mui/icons-material/GppGood";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import FeedbackIcon from "@mui/icons-material/Feedback";

import CheckIcon from "@mui/icons-material/Check";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PersonIcon from "@mui/icons-material/Person";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";

const StyledBadge = styled(Badge)(({ theme, bgcolor = "" }) => ({
    "& .MuiBadge-badge": {
        height: 24,
        width: 24,
        borderRadius: "50%",
        backgroundColor: bgcolor || "#44b700",
        color: "#44b700",
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
        border: `1px solid ${theme.palette.background.paper}`,
        padding: "0 4px",
    },
}));

export const avatarStyle = {
    backgroundColor: CentricTheme.palette.primary.main,
    color: "#FFF",
};

const CENTAvatar = React.forwardRef(function CENTAvatar(props, ref) {
    const { type, disabled, badge, ...rest } = props;

    let display = (
        <div {...rest} ref={ref}>
            <IconAvatar disabled={disabled} type={type} />
        </div>
    );

    switch (badge) {
        case "checked":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <CheckIcon
                            alt="Primary"
                            sx={{ fontSize: 16, color: "#FFFFFF" }}
                        />
                    }
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );

            break;

        case "attached":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <AttachFileIcon
                            alt="Associated"
                            sx={{ fontSize: 16, color: "#FFFFFF" }}
                        />
                    }
                >
                    <IconAvatar disabled={disabled} />
                </StyledBadge>
            );

            break;

        case "person":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <PersonIcon
                            alt="Review"
                            sx={{
                                fontSize: 16,
                                color: "#FFFFFF",
                            }}
                        />
                    }
                    bgcolor="#2196F3"
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );
            break;
        case "warning":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <PriorityHighIcon
                            alt="Warning"
                            sx={{
                                fontSize: 16,
                                color: "#FFFFFF",
                            }}
                        />
                    }
                    bgcolor="#ff9800"
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );
            break;

        case "error":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <PriorityHighIcon
                            alt="Error"
                            sx={{
                                fontSize: 16,
                                color: "#FFFFFF",
                            }}
                        />
                    }
                    bgcolor="#BB313D"
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );
            break;

        default:
            break;
    }

    return display;
});

const IconAvatar = ({ type = null, disabled = false }) => {
    const opacity = disabled ? 0.38 : 1;
    let icon = <SourceIcon />;

    switch (type) {
        case "term":
            icon = <GppGoodIcon title="Terms" />;
            break;

        case "activity":
            icon = <EventNoteIcon title="Activity" />;
            break;

        case "signOff":
        case "appraisal":
            icon = <AssignmentOutlinedIcon title="Appraisal" />;
            break;

        case "password":
        case "task":
            icon = <TaskOutlinedIcon title="Task" />;
            break;

        case "leave":
            icon = <EventIcon title="Leave" />;
            break;

        case "absence":
            icon = <EventIcon title="Absence" />;
            break;

        case "notification":
            icon = <NotificationsNoneIcon title="Notification" />;
            break;

        case "subscription":
            icon = <SyncOutlinedIcon title="Subscription" />;
            break;

        case "MSFSurvey":
            icon = <AssessmentOutlinedIcon title="MSFSurvey" />;
            break;

        case "MSFQuestionnaire":
            icon = <FeedbackIcon title="MSFQuestionnaire" />;
            break;

        default:
            break;
    }

    return (
        <Avatar
            aria-label="Avatar"
            style={{ ...avatarStyle, opacity: opacity }}
            sx={{ width: 52, height: 52, marginRight: "4px" }}
        >
            {icon}
        </Avatar>
    );
};

export default CENTAvatar;
